import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CalendarCheck, PartyPopper, ShoppingBag, UtensilsCrossed, Lock } from "lucide-react";
import { api, formatApiError, fmtDate, fmtUSD } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/i18n/LanguageContext";
import Modal from "@/components/Modal";
import FoodRequestModal from "@/components/FoodRequestModal";
import { Label, Input, Textarea, PrimaryButton, GhostButton } from "@/components/fields";

const EMPTY = { name: "", email: "", phone: "", check_in: "", check_out: "", guests: 2, message: "" };

export default function VillaEnquiryModal({ open, onClose }) {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const { addItem } = useCart();
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [mealsOpen, setMealsOpen] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoteErr, setQuoteErr] = useState("");

  useEffect(() => {
    if (open) {
      setDone(null);
      setQuote(null);
      setQuoteErr("");
      setForm((f) => ({ ...EMPTY, name: user?.name || f.name, email: user?.email || f.email }));
    }
  }, [open, user]);

  useEffect(() => {
    if (!open || !form.check_in || !form.check_out || form.check_out <= form.check_in) {
      setQuote(null);
      setQuoteErr("");
      return;
    }
    const id = setTimeout(() => {
      api.get("/enquiries/quote", { params: { check_in: form.check_in, check_out: form.check_out } })
        .then((r) => { setQuote(r.data); setQuoteErr(""); })
        .catch((e) => { setQuote(null); setQuoteErr(formatApiError(e)); });
    }, 350);
    return () => clearTimeout(id);
  }, [form.check_in, form.check_out, open]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const addToCart = () => {
    if (!form.check_in || !form.check_out) return toast.error(t("enquiry.errDates"));
    if (form.check_out <= form.check_in) return toast.error(t("enquiry.errOrder"));
    if (quoteErr) return toast.error(quoteErr);
    addItem({
      kind: "villa",
      label: t("cart.villaStay"),
      check_in: form.check_in,
      check_out: form.check_out,
      guests: Number(form.guests),
      notes: form.message,
      lang,
    });
    toast.success(t("cart.added"));
    onClose();
  };

  const navigate = useNavigate();
  const bookNow = () => {
    if (!form.check_in || !form.check_out) return toast.error(t("enquiry.errDates"));
    if (form.check_out <= form.check_in) return toast.error(t("enquiry.errOrder"));
    if (quoteErr || !quote) return toast.error(quoteErr || t("enquiry.errDates"));
    addItem({
      kind: "villa",
      label: t("cart.villaStay"),
      check_in: form.check_in,
      check_out: form.check_out,
      guests: Number(form.guests),
      notes: form.message,
      lang,
    });
    onClose();
    navigate("/cart");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.check_in || !form.check_out) return toast.error(t("enquiry.errDates"));
    if (form.check_out <= form.check_in) return toast.error(t("enquiry.errOrder"));
    if (quoteErr) return toast.error(quoteErr);
    setSending(true);
    try {
      const { data } = await api.post("/enquiries", { ...form, guests: Number(form.guests), lang });
      setDone(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
    <Modal open={open} onClose={onClose} title={done ? t("enquiry.doneTitle") : t("enquiry.title")} testid="villa-enquiry-modal">
      {done ? (
        <div className="text-center py-6 space-y-4" data-testid="villa-enquiry-success">
          <div className="w-16 h-16 mx-auto rounded-full bg-teal/10 flex items-center justify-center">
            <PartyPopper className="text-teal" size={28} />
          </div>
          <h4 className="font-serif text-2xl text-ink">{t("enquiry.thanks", { name: done.name.split(" ")[0] })}</h4>
          <p className="text-sm text-ink/60 leading-relaxed max-w-sm mx-auto">
            <strong>{fmtDate(done.check_in)}</strong> → <strong>{fmtDate(done.check_out)}</strong> ({done.guests} {t("enquiry.guestsWord")}
            {done.nights ? `, ${done.nights} ${t("enquiry.nightsWord")}` : ""}) {t("enquiry.successCopy")}{" "}
            <strong>{done.email}</strong>.
          </p>
          {done.quoted_total && (
            <p className="text-sm text-ink/70" data-testid="enquiry-success-quote">
              {t("enquiry.estimated")} <span className="font-serif text-2xl text-terracotta">${fmtUSD(done.quoted_total)}</span>
            </p>
          )}
          <PrimaryButton data-testid="villa-enquiry-done-btn" onClick={onClose} className="mx-auto">
            {t("enquiry.backToParadise")}
          </PrimaryButton>
          <div>
            <GhostButton data-testid="enquiry-add-meals-btn" onClick={() => setMealsOpen(true)} className="mx-auto">
              <UtensilsCrossed size={14} /> {t("food.addMeals")}
            </GhostButton>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4" data-testid="villa-enquiry-form">
          <p className="text-sm text-ink/60 -mt-1">{t("enquiry.intro")}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="enq-name">{t("enquiry.name")}</Label>
              <Input id="enq-name" data-testid="enquiry-name-input" required value={form.name} onChange={set("name")} placeholder={t("enquiry.namePh")} />
            </div>
            <div>
              <Label htmlFor="enq-email">{t("enquiry.email")}</Label>
              <Input id="enq-email" data-testid="enquiry-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@email.com" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="enq-checkin">{t("enquiry.checkin")}</Label>
              <Input id="enq-checkin" data-testid="enquiry-checkin-input" type="date" required value={form.check_in} onChange={set("check_in")} />
            </div>
            <div>
              <Label htmlFor="enq-checkout">{t("enquiry.checkout")}</Label>
              <Input id="enq-checkout" data-testid="enquiry-checkout-input" type="date" required value={form.check_out} onChange={set("check_out")} />
            </div>
          </div>

          {(quote || quoteErr) && (
            <div
              data-testid="enquiry-estimate"
              className={`rounded-2xl p-4 text-sm flex items-center justify-between ${
                quoteErr ? "bg-destructive/10 text-destructive" : "bg-teal/5 border border-teal/15 text-ink"
              }`}
            >
              {quoteErr ? (
                <span data-testid="enquiry-overlap-warning">{quoteErr}</span>
              ) : (
                <>
                  <span>{t("enquiry.nightsTimes", { nights: quote.nights, rate: Math.round(quote.total / quote.nights) })}</span>
                  <span className="font-serif text-2xl text-terracotta" data-testid="enquiry-estimate-total">${fmtUSD(quote.total)}</span>
                </>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="enq-guests">{t("enquiry.guests")}</Label>
              <Input id="enq-guests" data-testid="enquiry-guests-input" type="number" min="1" max="6" value={form.guests} onChange={set("guests")} />
            </div>
            <div>
              <Label htmlFor="enq-phone">{t("enquiry.phone")}</Label>
              <Input id="enq-phone" data-testid="enquiry-phone-input" value={form.phone} onChange={set("phone")} placeholder="+1 ..." />
            </div>
          </div>
          <div>
            <Label htmlFor="enq-message">{t("enquiry.notes")}</Label>
            <Textarea
              id="enq-message"
              data-testid="enquiry-message-input"
              value={form.message}
              onChange={set("message")}
              placeholder={t("enquiry.notesPh")}
            />
          </div>
          <PrimaryButton data-testid="enquiry-book-now-btn" type="button" onClick={bookNow}
                         disabled={!quote || !!quoteErr} className="w-full">
            <Lock size={15} /> {t("cart.bookNow")}{quote ? ` — $${fmtUSD(quote.total)}` : ""}
          </PrimaryButton>
          <div className="grid grid-cols-2 gap-2">
            <GhostButton data-testid="enquiry-add-cart-btn" type="button" onClick={addToCart} className="w-full">
              <ShoppingBag size={15} /> {t("cart.addBtn")}
            </GhostButton>
            <GhostButton data-testid="enquiry-submit-btn" type="submit" disabled={sending} className="w-full">
              <CalendarCheck size={15} /> {sending ? t("enquiry.sending") : t("enquiry.send")}
            </GhostButton>
          </div>
        </form>
      )}
    </Modal>
    <FoodRequestModal
      open={mealsOpen}
      onClose={() => setMealsOpen(false)}
      prefill={done ? { reservation_ref: done.id.slice(0, 8).toUpperCase(), start_date: done.check_in, end_date: done.check_out } : null}
    />
    </>
  );
}
